import React, { useState, useEffect } from "react";
import { Loader2, Globe, ExternalLink, ChevronDown, Youtube } from "lucide-react";
import { validateImageUrl } from '../utils/metadata';

const PreviewSection = ({ 
  isLoading, 
  metadata, 
  isDarkMode,
  expandedHeight = false 
}) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);

  useEffect(() => {
    const tryImageUrls = async () => {
      if (metadata?.isYoutube && metadata.videoId) {
        const qualities = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault', 'default'];
        for (const quality of qualities) {
          const url = `https://img.youtube.com/vi/${metadata.videoId}/${quality}.jpg`;
          if (await validateImageUrl(url)) {
            setCurrentImageUrl(url);
            return;
          }
        }
      } else if (metadata?.images?.[selectedImage]?.url) {
        setCurrentImageUrl(metadata.images[selectedImage].url);
      }
    };

    tryImageUrls();
  }, [metadata, selectedImage]);

  const handlePreviewClick = (index) => {
    // Voor geüploade afbeeldingen
    if (metadata?.images?.[index]?.url && !metadata.url) {
      window.open(metadata.images[index].url, '_blank', 'noopener,noreferrer');
    } 
    // Voor alle andere content (YouTube, Instagram, websites etc)
    else if (metadata?.url) {
      window.open(metadata.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleThumbnailClick = (index) => {
    setSelectedImage(index);
  };

  return (
    <div className={`border rounded-lg p-4 ${
      isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
    }`}>
      <h3 className={`font-medium mb-4 ${
        isDarkMode ? 'text-gray-300' : 'text-gray-700'
      }`}>Preview</h3>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className={`animate-spin ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} size={32} />
        </div>
      ) : metadata ? (
        <div className="space-y-4">
          {/* Main Image Preview */}
          <div className="relative">
            <button 
              onClick={() => handlePreviewClick(selectedImage)}
              className="w-full group relative"
            >
              <div className={`relative ${
                expandedHeight ? 'h-80' : 'h-48'
              } rounded-lg flex items-center justify-center ${
                isDarkMode ? 'bg-gray-900' : 'bg-gray-100'
              }`}>
                {currentImageUrl ? (
                  <>
                    <img
                      src={currentImageUrl}
                      alt={metadata.title}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e) => {
                        if (metadata?.isYoutube && metadata.videoId) {
                          const currentQuality = e.target.src.split('/').pop().split('.')[0];
                          const qualities = ['maxresdefault', 'sddefault', 'hqdefault', 'mqdefault', 'default'];
                          const currentIndex = qualities.indexOf(currentQuality);
                          if (currentIndex < qualities.length - 1) {
                            e.target.src = `https://img.youtube.com/vi/${metadata.videoId}/${qualities[currentIndex + 1]}.jpg`;
                            return;
                          }
                        }
                        e.target.style.display = 'none';
                        const container = e.target.parentNode;
                        if (metadata.favicon) {
                          container.innerHTML = `<img src="${metadata.favicon}" class="w-16 h-16" alt="${metadata.title}" />`;
                        }
                      }}
                    />
                    {metadata.isYoutube && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                        <div className="bg-red-600 rounded-lg p-3 bg-opacity-90">
                          <Youtube size={32} className="text-white" />
                        </div>
                      </div>
                    )}
                  </>
                ) : metadata.favicon ? (
                  <img 
                    src={metadata.favicon}
                    className="w-16 h-16"
                    alt={metadata.title}
                  />
                ) : (
                  <Globe size={48} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                  <ExternalLink className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={24} />
                </div>
              </div>
            </button>
          </div>

          {/* Thumbnails voor meerdere afbeeldingen */}
          {!metadata.isYoutube && metadata.images?.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {metadata.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 ${
                    selectedImage === index 
                      ? 'ring-2 ring-blue-500' 
                      : 'hover:opacity-80'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {metadata.isYoutube && metadata.channelTitle && (
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {metadata.channelTitle}
            </div>
          )}

          <h4 className={`font-bold text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
            {metadata.title}
          </h4>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {metadata.description}
          </p>
        </div>
      ) : (
        <div className={`flex items-center justify-center h-48 ${
          isDarkMode ? 'text-gray-500' : 'text-gray-400'
        }`}>
          Voer een URL in om de preview te zien
        </div>
      )}
    </div>
  );
};

export default PreviewSection;